import { useTranslation } from 'react-i18next'
import { toast } from '@/components/ui/toast'
import { ConfirmDeleteDialog } from '@/components/shared'
import { useActivateClient, useDeactivateClient } from '@/hooks/useClients'
import { getApiErrorMessage } from '@/services/api'
import type { Client } from '@/types'

interface Props {
  client: Client | null
  // 'deactivate' suspends the client; 'activate' re-enables it.
  action: 'activate' | 'deactivate'
  onClose: () => void
  onDone?: () => void
}

// 2.4.1 Activate / deactivate — same confirm chrome as delete (Figma 1336:31123).
export function ToggleClientStatusDialog({
  client,
  action,
  onClose,
  onDone,
}: Props) {
  const { t } = useTranslation('clients')
  const activate = useActivateClient()
  const deactivate = useDeactivateClient()
  const pending = activate.isPending || deactivate.isPending
  const isDeactivate = action === 'deactivate'

  const confirm = async () => {
    if (!client) return
    try {
      if (isDeactivate) {
        await deactivate.mutateAsync(client.id)
        toast({ variant: 'success', title: t('toggleStatus.deactivated') })
      } else {
        await activate.mutateAsync(client.id)
        toast({ variant: 'success', title: t('toggleStatus.activated') })
      }
      onDone?.()
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(
          e,
          isDeactivate
            ? t('toggleStatus.deactivateError')
            : t('toggleStatus.activateError')
        ),
      })
    }
  }

  return (
    <ConfirmDeleteDialog
      open={!!client}
      onOpenChange={o => !o && onClose()}
      title={
        isDeactivate
          ? t('toggleStatus.deactivateTitle')
          : t('toggleStatus.activateTitle')
      }
      description={
        isDeactivate
          ? t('toggleStatus.confirmDeactivate', { name: client?.name ?? '' })
          : t('toggleStatus.confirmActivate', { name: client?.name ?? '' })
      }
      confirmLabel={
        isDeactivate
          ? t('toggleStatus.confirmDeactivateButton')
          : t('toggleStatus.confirmActivateButton')
      }
      confirmVariant={isDeactivate ? 'danger' : 'brand'}
      onConfirm={confirm}
      busy={pending}
    />
  )
}

export default ToggleClientStatusDialog
